import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchWaitingForMe } from "services/invoice-inbox.service";
import { InvoiceWaitingDoc } from "types/InvoiceInbox";

/**
 * The purchase invoices waiting for the CURRENT user's approval, per company
 * (erp-backend GET invoice-inbox/docs/waiting-for-me), for the "Sąskaitų
 * tvirtinimas" queue page of the task list. The number on the bell comes from
 * the cabinet summary.
 *
 * `companyId` is recorded with the data so a stale list from the previous
 * company never shows under the next one.
 */
export interface InvoiceApprovalsState {
  companyId: string | null;
  count: number;
  docs: InvoiceWaitingDoc[];
  isLoading: boolean;
  isError: boolean;
}

const initialState: InvoiceApprovalsState = {
  companyId: null,
  count: 0,
  docs: [],
  isLoading: false,
  isError: false,
};

const invoiceApprovalsSlice = createSlice({
  name: "invoice-approvals",
  initialState,
  reducers: {
    resetInvoiceApprovals() {
      return initialState;
    },
    setInvoiceApprovalsLoading(state, action: PayloadAction<{ companyId: string }>) {
      if (state.companyId !== action.payload.companyId) {
        state.companyId = action.payload.companyId;
        state.count = 0;
        state.docs = [];
      }
      state.isLoading = true;
      state.isError = false;
    },
    setInvoiceApprovals(
      state,
      action: PayloadAction<{ companyId: string; count: number; docs: InvoiceWaitingDoc[] }>
    ) {
      state.companyId = action.payload.companyId;
      state.count = action.payload.count;
      state.docs = action.payload.docs;
      state.isLoading = false;
      state.isError = false;
    },
    setInvoiceApprovalsError(state, action: PayloadAction<{ companyId: string }>) {
      state.companyId = action.payload.companyId;
      state.isLoading = false;
      state.isError = true;
    },
  },
});

const { actions, reducer } = invoiceApprovalsSlice;

export const {
  resetInvoiceApprovals,
  setInvoiceApprovalsLoading,
  setInvoiceApprovals,
  setInvoiceApprovalsError,
} = actions;

export default reducer;

/**
 * Loads the queue for one company. Callers gate it on register access
 * (`Master` or `Access invoice inbox` in the role's permission groups): the
 * endpoint answers 403 to anyone else, and a 403 per page load would be noise
 * in the audit log for the majority of employees who never see the register.
 */
// Only the LATEST request may write: switch from company A to B while A's
// fetch is still running and B's answer must not be overwritten by A's late
// one (which would leave B's list at A's answer until the next fetch). The
// same guard makes a post-decision reload win over an older in-flight fetch.
let latestRequest = 0;

export const fetchInvoiceApprovalsQueue = (companyId: string) => async (dispatch: any) => {
  const requestId = ++latestRequest;
  dispatch(setInvoiceApprovalsLoading({ companyId }));
  try {
    const data = await fetchWaitingForMe(companyId);
    if (requestId !== latestRequest) return;
    dispatch(
      setInvoiceApprovals({
        companyId,
        count: Number(data.count) || 0,
        docs: Array.isArray(data.docs) ? data.docs : [],
      })
    );
  } catch (e) {
    if (requestId !== latestRequest) return;
    dispatch(setInvoiceApprovalsError({ companyId }));
  }
};

/** True when this role may read the register (and therefore its queue). */
export function canSeeInvoiceInbox(permissionsgroups?: string[] | null): boolean {
  const groups = permissionsgroups || [];
  return groups.includes("Master") || groups.includes("Access invoice inbox");
}

type RoleLike = { permissionsgroups?: string[] | null; company?: string | null } | null | undefined;

/**
 * The role in the store is the LAST one fetched, and it is not cleared on a
 * company switch — for a moment under company B it is still A's role. A
 * role is "for" a company when its own `company` says so (the get-role
 * response is the CompaniesUsers row); a role without the field (older
 * payloads, test stubs) is taken at face value.
 */
export function isRoleForCompany(role: RoleLike, companyId?: string | null): boolean {
  if (!role || !companyId) return false;
  return !role.company || role.company === companyId;
}

/** Register access for THIS company: the role is for it and grants it. */
export function canSeeInvoiceInboxFor(role: RoleLike, companyId?: string | null): boolean {
  return isRoleForCompany(role, companyId) && canSeeInvoiceInbox(role?.permissionsgroups);
}
